import React from 'react'
// libs
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
// components
import { IconButton, Menu } from 'components/UI'
// icons
import PublishIcon from '@mui/icons-material/Publish'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
// utils
import pluralize from 'pluralize'
import { handleError } from 'utils/errors/handlers'
import { readSpreadsheetRows, toCellString, SPREADSHEET_ACCEPT } from 'utils/csv'

const FileInput = styled.input`
  display: none;
`

const MenuTrigger = styled.span<{ disabled?: boolean }>`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`

const ExampleLink = styled.a`
  color: inherit;
  text-decoration: none;
`

interface IBulkImportItem {
  code: string
  description: string
  parentCode: string
}

interface IProps {
  entity: string
  mutation: (options: {
    variables: { input: { items: IBulkImportItem[] } }
    context?: Record<string, unknown>
  }) => Promise<unknown>
  onImported: () => Promise<unknown> | unknown
}

const HEADER_MATCHERS = [/code/i, /description/i, /parent/i]

const isHeaderRow = (row: string[]): boolean =>
  HEADER_MATCHERS.some((matcher, index) => matcher.test(String(row[index] ?? '')))

const parseRowsToItems = (rows: unknown[][]): IBulkImportItem[] =>
  rows
    .filter((row, index) => !(index === 0 && isHeaderRow(row as string[])))
    .map((row) => ({
      code: toCellString(row[0]),
      description: toCellString(row[1]),
      parentCode: toCellString(row[2])
    }))

const ImportCsvButton: React.FC<IProps> = ({ entity, mutation, onImported }) => {
  const inputFileRef = React.useRef<HTMLInputElement>(null)

  const [isLoading, setIsLoading] = React.useState(false)

  const { enqueueSnackbar } = useSnackbar()

  const onButtonClick = () => inputFileRef.current?.click()

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    event.target.value = null

    if (!file) {
      return
    }

    setIsLoading(true)

    try {
      const rows = await readSpreadsheetRows(file)

      const items = parseRowsToItems(rows)

      if (!items.length) {
        enqueueSnackbar('No rows found in the file', { variant: 'warning' })
        return
      }

      await mutation({ variables: { input: { items } } })

      await onImported()

      enqueueSnackbar(`Imported ${items.length} ${pluralize('row', items.length)}`, { variant: 'success' })
    } catch (error) {
      handleError(error, 'ImportCsvButton.onFileChange', { notify: true })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Menu
        trigger={
          <MenuTrigger disabled={isLoading}>
            <IconButton preset='PURPLE' isActive description='Import' disabled={isLoading} onClick={() => {}}>
              <PublishIcon />
            </IconButton>
          </MenuTrigger>
        }>
        <span onClick={onButtonClick}>
          <PublishIcon />
          Import {entity}
        </span>
        <ExampleLink href={'/files/import_example.csv'} download='import_example.csv'>
          <FileDownloadIcon />
          Download Sample
        </ExampleLink>
      </Menu>

      <FileInput type='file' ref={inputFileRef} onChange={onFileChange} accept={SPREADSHEET_ACCEPT} />
    </>
  )
}

export default ImportCsvButton
